(ns story-builder.story-editor.animation-sidebar.util
  (:require
   ["animejs" :as anime]
   ["react" :as react]
   [camel-snake-kebab.core :as csk]
   [clojure.string :as str]
   [reagent.core :as r]
   [story-builder.component.wrapper :as wrapper]
   [story-builder.define :as define]
   [story-builder.story-editor.animation-sidebar.constant :as animation-sidebar.constant]
   [story-builder.util.core :as util]
   [story-builder.util.dom :as util.dom]
   [story-builder.util.math :as util.math]))

(def animation-has-direction? #{"fly-in-top" "fly-in-right" "fly-in-bottom" "fly-in-left" "rotate-in-right" "rotate-in-left" "drop"})

(defn animation-vars
  [{:keys [animation layer-rect scale-factor scene-rect]
    :or   {scale-factor 1}}]
  (let [{:keys          [start-position
                         zoom-factor]
         animation-type :animation
         :or            {start-position  :scene
                         zoom-factor 3}} animation]
    (merge

     (when (contains? #{"zoom-in" "zoom-out"} animation-type)
       {(str "--animation-" (csk/->camelCase animation-type) "-scaleVal") zoom-factor})
     (when layer-rect
       (let [direction    (when (animation-has-direction? animation-type)
                            (case animation-type
                              "fly-in-top"      :top
                              "fly-in-right"    :right
                              "fly-in-bottom"   :bottom
                              "fly-in-left"     :left
                              "rotate-in-right" :right
                              "rotate-in-left"  :left
                              "drop"            :top))
             rotation     (:rotation layer-rect)
             layer-points (cond->> (-> layer-rect (util.math/unrotate-rect) (util.math/rect->corners))
                            (not (zero? rotation))
                            (mapv #(util.math/rotate-around % (util.math/rect->center layer-rect) rotation)))
             scene-points (util.math/rect->corners scene-rect)
             offset       (if direction
                            (case start-position
                              :layer
                              (let [{:keys [width height]} (util.math/unrotate-rect layer-rect)]
                                (case direction
                                  :top
                                  (- height)
                                  :right
                                  width
                                  :bottom
                                  height
                                  :left
                                  (- width)))
                              :scene
                              (cond-> (util.math/binary-search-move layer-points scene-points (- (case direction :top 90 :right 0 :bottom 270 :left 180) rotation))
                                (contains? #{:top :left} direction)
                                (-))
                              :custom
                              0)
                            0)]
         (when offset
           {(str "--animation-" (csk/->camelCase animation-type) "-startVal") (str (* offset scale-factor) "px")}))))))

(defn story-config-animation-vars
  [story-config {:keys [current-idx layer-ids]}]
  (reduce
   (fn [res [idx scene]]
     (let [layers-with-animations (filter #(or (:animations %) (:animation %))
                                          (cond->> (:layers scene)
                                            layer-ids
                                            (filter #(contains? layer-ids (:id %)))))]
       (reduce
        (fn [res {:keys [animations animation id]}]
          (if-let [layer-el (js/document.getElementById id)]
            (let [scene-rect   (util.dom/rect (js/document.getElementById (:id scene)))
                  layer-rect   (util.dom/rect layer-el)
                  scale-factor (if (and current-idx (not= current-idx idx)) 1.11111 1)]
              (assoc res id (reduce
                             (fn [res animation]
                               (assoc res (:id animation)
                                      (animation-vars {:animation    animation
                                                       :layer-rect   layer-rect
                                                       :scene-rect   scene-rect
                                                       :scale-factor scale-factor})))
                             {}
                             (or animations [{:animation animation}]))))
            res))
        res layers-with-animations)))
   {}
   (util/seq-indexed (:scenes story-config))))

(defn animation-chain
  ([animations id]
   (animation-chain animations id []))
  ([animations id chain]
   (if-let [current-animation (get animations id)]
     (recur animations (:animate-after current-animation) (conj chain current-animation))
     chain)))

(defn animations-by-id [layers]
  (->> (mapcat :animations layers)
       (map (juxt :id identity))
       (into {})))

(defn update-animation-delays [layers]
  (let [animations (animations-by-id layers)]
    (letfn [(update-layer [layer]
              (letfn [(update-animation [{:keys [animate-after]
                                          :as   animation}]
                        (if animate-after
                          (let [chain (animation-chain animations animate-after)]
                            (reduce
                             (fn [res {:keys [delay
                                              trigger
                                              duration]
                                       :or   {trigger :auto}}]
                               (cond-> (update res :delay + (+ delay duration))
                                 (not= trigger :auto)
                                 (assoc :trigger trigger)))
                             animation
                             chain))
                          animation))]
                (update layer :animations (partial mapv update-animation))))]
      (mapv update-layer layers))))

(def click-count-path [:animations :click-counts])
(def hover-count-path [:animations :hover-counts])
(def count-path [:animations :count])
(def iterations-path [:animations :iterations])
(def scene-interaction-iterations-path [:animations :scene-interaction-iterations])
(def scene-interaction-count-path [:animations :scene-interaction-count])

(defn animation-span
  [_ {:keys [scene-idx
             story-state]}]
  (let [click-count-c                  (r/cursor story-state click-count-path) ; FIXME Probably don't need separate count as each animation has a unique id/trigger.
        hover-count-c                  (r/cursor story-state hover-count-path) ; FIXME Probably don't need separate count as each animation has a unique id/trigger.
        count-c                        (r/cursor story-state count-path) ; Store all counts in one location.
        iterations-c                   (r/cursor story-state iterations-path)
        scene-interaction-iterations-c (r/cursor story-state (conj scene-interaction-iterations-path scene-idx))
        scene-interaction-count-c      (r/cursor story-state (conj scene-interaction-count-path scene-idx))
        current-idx-c                  (r/cursor story-state [:current-idx])
        paused?-c                      (r/cursor story-state [:paused?])]
    (r/create-class
     {:reagent-render
      (fn [{:keys [animate-after
                   delay
                   direction
                   duration
                   id
                   iterations
                   loop-indefinitely?
                   split-by
                   timing-function
                   trigger
                   trigger-repeat?
                   version]
            :as    animation
            animation-name :animation
            :or            {split-by        :whole-layer
                            trigger         :auto
                            trigger-repeat? false}}
           {:keys [child
                   idx
                   link?
                   res
                   vars]}]
        (let [trigger         (if (and (= trigger :page-scroll)
                                       (not (:tag-data @story-state)))
                                :auto
                                trigger)
              reset-animation (fn [id animation trigger repeat?]
                                (when repeat?
                                  (.cancel animation)
                                  (set! (.-currentTime animation) 0))
                                (case trigger
                                  :scene-interaction
                                  (swap! scene-interaction-iterations-c assoc id @scene-interaction-count-c)
                                  (swap! iterations-c update id (fnil inc 0))))
              text-split?     (and split-by (not= split-by :whole-layer))]
          [:span
           (merge {:id (str "animation-" id)}
                  (when-not text-split?
                    {:style            (merge {:animation-delay           (str delay "s")
                                               :animation-direction       direction
                                               :animation-duration        (str duration "s")
                                               :animation-timing-function timing-function
                                               :animation-iteration-count (if loop-indefinitely?
                                                                            "infinite"
                                                                            iterations)
                                               :animation-play-state      (if (and story-state @paused?-c)
                                                                            "paused"
                                                                            (cond
                                                                              (= trigger :auto)
                                                                              "running"
                                                                              (and (= trigger :scene-interaction)
                                                                                   (not= @scene-interaction-count-c
                                                                                         (get @scene-interaction-iterations-c id)))
                                                                              "running"
                                                                              (and (= trigger :hover)
                                                                                   (if animate-after
                                                                                     (not= (get @hover-count-c animate-after) (get @iterations-c id))
                                                                                     (not= (get @hover-count-c id) (get @iterations-c id))))
                                                                              "running"
                                                                              (and (= trigger :click)
                                                                                   (if animate-after
                                                                                     (not= (get @click-count-c animate-after) (get @iterations-c id))
                                                                                     (not= (get @click-count-c id) (get @iterations-c id))))
                                                                              "running"
                                                                              (and (= trigger :page-scroll)
                                                                                   (not= (get @count-c :page-scroll) (get @iterations-c id)))
                                                                              "running"
                                                                              :else
                                                                              "paused"))}
                                              (animation-vars {:animation animation})
                                              (get vars id))
                     :data-animation   animation-name
                     :on-animation-end (fn [e]
                                         (let [current-target (.-currentTarget e)
                                               target         (.-target e)
                                               animation      (first (.getAnimations current-target))]
                                           (when (= current-target target)
                                             (let [id (str/replace (.getAttribute current-target "id") "animation-" "")]
                                               (reset-animation id animation trigger trigger-repeat?)))))})
                  (when version
                    {:data-animation-version version})
                  (when (and (contains? #{:click :hover} trigger)
                             (= @current-idx-c scene-idx))
                    (merge {:class "u-poev-a"}
                           (cond
                             (and (= trigger :click)
                                  (= (get @click-count-c id) (get @iterations-c id)))
                             (let [click-handler (fn []
                                                   (when-not link?
                                                     (swap! scene-interaction-count-c inc))
                                                   (swap! click-count-c update id inc))]
                               {:on-click click-handler})
                             (and (= trigger :hover)
                                  (= (get @hover-count-c id) (get @iterations-c id)))
                             (let [event-handler (fn [_]
                                                   (swap! hover-count-c update id inc))]
                               (if (define/mobile?)
                                 {:on-click event-handler}
                                 {:on-mouse-enter event-handler})))))
                  (when (and (zero? idx) (map? child))
                    child))
           res]))})))

(defn wrap-with-animations-inner
  [{:keys [scene-idx
           story-state
           link?]
    :or   {story-state (r/atom {})}} _]
  (r/create-class
   {:reagent-render
    (fn [{:keys [layer-animations
                 vars]}
         child]
      (reduce
       (fn [res [idx {:keys [id] :as animation}]]
         ^{:key id}
         [animation-span animation
          {:idx         idx
           :link?       link?
           :res         res
           :scene-idx   scene-idx
           :story-state story-state
           :child       (when (map? child) child)
           :vars        vars}])
       (when (vector? child) child)
       (util/seq-indexed (reverse layer-animations))))}))

(defn wrap-with-animations
  [arg1 arg2]
  [wrap-with-animations-inner arg1 arg2])

(defn randomize-animation-ids [layer]
  (cond-> layer
    (:animations layer)
    (update :animations
            (fn [animations]
              (mapv
               (fn [animation]
                 (assoc animation :id (str (random-uuid))))
               animations)))))

(defn split-text [text split-type]
  (case split-type
    :letter (map str text)
    :word (if (empty? text)
            []
            (let [matches (re-seq #"(\S+)(\s*)" text)
                  pieces  (mapcat (fn [[_ word space]]
                                    [(str word) space]) matches)]
              ;; Check if text ends with non-space (no trailing space captured)
              (if (re-find #"\S$" text)
                (butlast pieces)
                pieces)))
    :sentence (if (empty? text)
                []
                (let [matches (re-seq #"([^.!?]+[.!?])(\s*)" text)
                      pieces (mapcat (fn [[_ sentence space]]
                                       [(str sentence) space]) matches)]
                  ;; Check if text ends with non-space (no trailing space captured)
                  (if (re-find #"[^.!?\s]$" text)
                    (conj (vec (butlast pieces)) (re-find #"[^.!?]+$" text))
                    pieces)))
    [text]))

(defn wrap-text
  "Transforms a Hiccup form by wrapping text in [:span.letter/.word/.sentence] tags.
   Preserves the structure including attribute maps.
   split-type can be :letter, :word, or :sentence"
  [hiccup split-type]
  (let [class-name (name split-type)]
    (letfn [(transform-node [node]
              (cond
                (string? node)
                (let [parts (split-text node split-type)]
                  (if (= split-type :letter)
                    ;; For letter-based splitting, wrap letters with word containers
                    (let [word-parts    (partition-by #(re-matches #"\s+" %) parts)
                          wrapped-words (map-indexed
                                         (fn [word-idx word-part]
                                           (if (re-matches #"\s+" (first word-part))
                                             ;; If it's whitespace, return it directly
                                             (apply str word-part)
                                             ;; If it's a word, wrap its letters
                                             [:span.word-container
                                              {:key   (str "word-" word-idx)
                                               :style {:display     "inline-block"
                                                       :white-space "normal"}}
                                              (map-indexed
                                               (fn [letter-idx letter]
                                                 [:span
                                                  {:key   (str "letter-" word-idx "-" letter-idx)
                                                   :class class-name
                                                   :style {:display "inline-block"
                                                           :opacity 0}}
                                                  letter])
                                               word-part)]))
                                         word-parts)]
                      wrapped-words)
                    ;; For words/sentences, maintain original logic
                    (mapv (fn [text-part i]
                            (if (and (odd? i) (seq text-part))
                              text-part
                              [:span
                               {:key (str class-name "-" i)
                                :class class-name
                                :style {:display "inline-block"
                                        :opacity 0}}
                               text-part]))
                          parts
                          (range))))
                (vector? node)
                (let [tag                (first node)
                      rest-nodes         (rest node)
                      process-child      (fn [child]
                                           (let [result (transform-node child)]
                                             (if (string? child)
                                               result
                                               [result])))
                      processed-children (mapcat process-child rest-nodes)]
                  (vec (cons tag processed-children)))
                :else node))]
      (transform-node hiccup))))

(defn use-text-animation
  [{:keys [hiccup
           split-type
           chosen
           story-state
           cursors]}]
  (let [root-ref           (react/createRef)
        animation-ref      (react/useRef nil)
        animation-type     (if (contains? animation-sidebar.constant/supported-text-animations (:animation chosen))
                             (:animation chosen)
                             "fade-in")
        {:keys [paused?-c
                scene-interaction-count-c
                scene-interaction-iterations-c
                hover-count-c
                iterations-c
                click-count-c
                count-c]}  cursors
        timing-function    (:timing-function chosen)
        id                 (:id chosen)
        direction          (:direction chosen :normal)
        loop-indefinitely? (:loop-indefinitely? chosen)
        iterations         (:iterations chosen)
        trigger            (:trigger chosen :auto)
        trigger-repeat?    (:trigger-repeat? chosen)
        animate-after      (:animate-after chosen)
        duration-default   (case split-type
                             :letter 0.45
                             :word 0.6
                             :sentence 0.8)
        duration           (* (:per-item-duration chosen duration-default) 1000)
        step-default       (case split-type
                             :letter 0.05
                             :word 0.15
                             :sentence 0.3)
        step               (* (:per-item-delay chosen step-default) 1000)
        base-delay         (* (:delay chosen 0) 1000)
        easing             (or (get animation-sidebar.constant/css-timing->anime-easing timing-function)
                               (get animation-sidebar.constant/animation-type->anime-easing animation-type)
                               "linear")
        loop?              (if loop-indefinitely?
                             (= trigger :auto)
                             (let [n (or (when (number? iterations) (js/Math.floor iterations)) 1)]
                               (max 0 (dec n))))
        play-state         (if story-state
                             (cond
                               @paused?-c
                               "paused"
                               (= trigger :auto)
                               "running"
                               (and (= trigger :scene-interaction)
                                    (not= @scene-interaction-count-c
                                          (get @scene-interaction-iterations-c id)))
                               "running"
                               (and (= trigger :hover)
                                    (if animate-after
                                      (not= (get @hover-count-c animate-after) (get @iterations-c id))
                                      (not= (get @hover-count-c id) (get @iterations-c id))))
                               "running"
                               (and (= trigger :click)
                                    (if animate-after
                                      (not= (get @click-count-c animate-after) (get @iterations-c id))
                                      (not= (get @click-count-c id) (get @iterations-c id))))
                               "running"
                               (and (= trigger :page-scroll)
                                    (not= (get @count-c :page-scroll) (get @iterations-c id)))
                               "running"
                               :else
                               "paused")
                             "running")]
    (react/useEffect
     (fn []
       (when-let [dom-node (.-current root-ref)]
         (let [elements  (.querySelectorAll dom-node (str "." (name split-type)))]
           (when (seq elements)
             (let [props     (get animation-sidebar.constant/animation->props animation-type)
                   animation (anime/animate
                              elements
                              (clj->js
                               (merge
                                props
                                {"duration"  duration
                                 "delay"     (fn [_el i]
                                               (let [cnt (.-length elements)
                                                     idx (if (contains? #{:reverse :alternate-reverse} direction)
                                                           (- (dec cnt) i)
                                                           i)]
                                                 (+ base-delay (* step idx))))
                                 "easing"    easing
                                 "loop"      loop?
                                 "direction" (case direction
                                               :normal "normal"
                                               :reverse "reverse"
                                               :alternate "alternate"
                                               :alternate-reverse "alternateReverse"
                                               "normal")
                                 "onComplete" (when story-state
                                                (fn [animation]
                                                  (when trigger-repeat?
                                                    (.pause animation)
                                                    (.seek animation 0))
                                                  (swap! iterations-c update id (fnil inc 0))))})))]
               (set! (.-current animation-ref) animation)
               (when (= play-state "paused")
                 (when (.-pause animation)
                   (.pause animation)))))))
       (fn []
         (set! (.-current animation-ref) nil)
         (anime/utils.remove (str "." (name split-type)))))
     #js [hiccup animation-type timing-function loop-indefinitely? iterations])
    (react/useEffect
     (fn []
       (when-let [animation (.-current animation-ref)]
         (case play-state
           "paused" (when (.-pause animation) (.pause animation))
           "running" (when (.-play animation) (.play animation))
           nil))
       js/undefined)
     #js [play-state])
    root-ref))

(defn animated-content-component
  [{:keys [animations
           hiccup
           cursors
           story-state]}]
  (let [split-by (some #(when (:split-by %) (:split-by %)) animations)
        chosen   (some #(when (:split-by %) %) animations)]
    (case split-by
      :letter
      (let [split-type :letter
            root-ref   (use-text-animation {:hiccup      hiccup
                                            :split-type  split-type
                                            :chosen      chosen
                                            :cursors     cursors
                                            :story-state story-state})]
        (-> (wrap-text hiccup split-type)
            (wrapper/add-ref-to-first-dom root-ref)
            (first)))
      :word
      (let [split-type :word
            root-ref   (use-text-animation {:hiccup      hiccup
                                            :split-type  split-type
                                            :chosen      chosen
                                            :cursors     cursors
                                            :story-state story-state})]
        (-> (wrap-text hiccup split-type)
            (wrapper/add-ref-to-first-dom root-ref)
            (first)))
      :sentence
      (let [split-type :sentence
            root-ref   (use-text-animation {:hiccup      hiccup
                                            :split-type  split-type
                                            :chosen      chosen
                                            :cursors     cursors
                                            :story-state story-state})]
        (-> (wrap-text hiccup split-type)
            (wrapper/add-ref-to-first-dom root-ref)
            (first)))
      hiccup)))

(defn animate-content
  [{:keys [story-state scene-idx]}]
  (let [paused?-c                      (when story-state (r/cursor story-state [:paused?]))
        scene-interaction-count-c      (when story-state (r/cursor story-state (conj scene-interaction-count-path scene-idx)))
        scene-interaction-iterations-c (when story-state (r/cursor story-state (conj scene-interaction-iterations-path scene-idx)))
        hover-count-c                  (when story-state (r/cursor story-state hover-count-path))
        iterations-c                   (when story-state (r/cursor story-state iterations-path))
        click-count-c                  (when story-state (r/cursor story-state click-count-path))
        count-c                        (when story-state (r/cursor story-state count-path))
        current-idx-c                  (when story-state (r/cursor story-state [:current-idx]))]
    (fn [props]
      ^{:key (and current-idx-c @current-idx-c)}
      [:f> animated-content-component
       (assoc props :cursors {:paused?-c                      paused?-c
                              :scene-interaction-count-c      scene-interaction-count-c
                              :scene-interaction-iterations-c scene-interaction-iterations-c
                              :hover-count-c                  hover-count-c
                              :iterations-c                   iterations-c
                              :click-count-c                  click-count-c
                              :count-c                        count-c})])))